export type QuoteDiscountType = 'percent' | 'absolute';
export type QuoteDiscountApplicationMode = 'total' | 'distributed';
// F2-Nummerierung: 'manual' = OZ bleiben wie sie sind (Bestandsschutz, Default —
// importierte GAEB-/PDF-LV-OZ werden NIE still umnummeriert); 'auto' = der Nutzer hat
// "Neu nummerieren" geklickt, Strukturänderungen nummerieren seither automatisch nach
// (shared/quote-numbering.ts ist die einzige Vergabe-Quelle).
export type QuoteNumberingMode = 'manual' | 'auto';

export interface PersistedQuoteEditorState {
  discountAmount: number;
  discountType: QuoteDiscountType;
  discountApplicationMode: QuoteDiscountApplicationMode;
  originalPrices: Record<number, number>;
  isDiscountAppliedToPrices: boolean;
  numberingMode: QuoteNumberingMode;
}

export const DEFAULT_PERSISTED_QUOTE_EDITOR_STATE: PersistedQuoteEditorState = {
  discountAmount: 0,
  discountType: 'percent',
  discountApplicationMode: 'total',
  originalPrices: {},
  isDiscountAppliedToPrices: false,
  numberingMode: 'manual',
};

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export function normalizePersistedQuoteEditorState(
  input?: Partial<PersistedQuoteEditorState> | null,
): PersistedQuoteEditorState {
  const discountAmountRaw =
    typeof input?.discountAmount === 'number'
      ? input.discountAmount
      : Number(input?.discountAmount ?? DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountAmount);

  const discountAmount = Number.isFinite(discountAmountRaw)
    ? Math.max(0, roundCurrency(discountAmountRaw))
    : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountAmount;

  const discountType: QuoteDiscountType =
    input?.discountType === 'absolute' ? 'absolute' : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountType;

  const discountApplicationMode: QuoteDiscountApplicationMode =
    input?.discountApplicationMode === 'distributed'
      ? 'distributed'
      : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.discountApplicationMode;

  const rawOriginalPrices =
    input?.originalPrices && typeof input.originalPrices === 'object'
      ? input.originalPrices
      : DEFAULT_PERSISTED_QUOTE_EDITOR_STATE.originalPrices;

  const originalPrices: Record<number, number> = {};
  for (const [rawKey, rawValue] of Object.entries(rawOriginalPrices)) {
    const key = Number(rawKey);
    const value = Number(rawValue);

    if (!Number.isFinite(key) || !Number.isFinite(value)) {
      continue;
    }

    originalPrices[key] = Math.max(0, roundCurrency(value));
  }

  return {
    discountAmount,
    discountType,
    discountApplicationMode,
    originalPrices,
    isDiscountAppliedToPrices: Boolean(input?.isDiscountAppliedToPrices),
    // Nur explizites 'auto' aktiviert die automatische Neuvergabe — alles andere
    // (fehlend, unbekannt, Bestandsdaten) bleibt 'manual' (Bestandsschutz).
    numberingMode: input?.numberingMode === 'auto' ? 'auto' : 'manual',
  };
}
